import React from "react";


import {
	isNonEmptyMap,
	isFunction,
} from "@cayuse/shared-react.utils.type-checks";
import { v4 as uuidv4 } from "uuid";

import { deepCopy } from "components/flowable/ResearchTeam/utils/helpers";
import {
	CAYUSE_TYPES,
	NEW_RULE_ID,
	NEW_RULE_TYPES,
	RULE_STRUCTURE_TYPES,
} from "globals/constants";
import { updateRules } from "store/actions/form";

import {
	convertRules,
	getFormElementType,
} from "../List/ConditionBuilder/utils";
import { RuleBuilderContext } from "./RuleBuilderContext";

const MAX_ORDER_INDEX = 99;

export const useRuleBuilderContext = () => {
	const ctx = React.useContext(RuleBuilderContext);

	const handleRuleChange = changedRule => {
		const updatedRules = ctx.rules.map(rule => {
			const isChangedRule = rule.id === changedRule.id;

			return isChangedRule
				? {
					...rule,
					...changedRule,
				}
				: rule;
		});

		if (isFunction(ctx.onRuleChange)) {
			ctx.onRuleChange(changedRule);
		}

		ctx.setRules(updatedRules);
	};

	const sortRules = () => {
		const sortedRules = ctx.rules
			.map(rule => {
				const orderIndex = ctx.ruleOrderIndexes.has(rule.id)
					? ctx.ruleOrderIndexes.get(rule.id)
					: rule.orderIndex;

				return {
					...rule,
					orderIndex,
				};
			})
			.sort((a, b) => {
				return parseInt(a.orderIndex, 10) <= parseInt(b.orderIndex, 10)
					? -1
					: 1;
			});

		ctx.setRules(sortedRules);

		const promise = updateRules(ctx.formId, sortedRules).then(() => {
			ctx.setHasUnsavedOrderChanges(false);
		});

		return promise;
	};

	const getNewRuleIndex = () => {
		let highestOrderIndex = 0;

		if (isNonEmptyMap(ctx.ruleOrderIndexes)) {
			ctx.ruleOrderIndexes.forEach(ruleOrderIndex => {
				const orderIndex = parseInt(ruleOrderIndex, 10);

				if (
					orderIndex > highestOrderIndex &&
                    orderIndex < MAX_ORDER_INDEX
				) {
					highestOrderIndex = orderIndex;
				}
			});
		}

		return highestOrderIndex === MAX_ORDER_INDEX - 1
			? MAX_ORDER_INDEX - 1
			: highestOrderIndex + 1;
	};

	const getOptionType = (formElementId, optionId) => {
		const formElement =
            ctx.formElements.find(element => element.id === formElementId) ??
            {};
		if ("*" === optionId) {
			return NEW_RULE_TYPES.ANSWERED;
		} else if (CAYUSE_TYPES.RESEARCH_TEAM === formElement.cayuseType) {
			return NEW_RULE_TYPES.RT_ROLE;
		} else {
			return NEW_RULE_TYPES.EXACT;
		}
	};

	const addRule = ({ formElementId, optionId }) => {
		const rule = {
			id: NEW_RULE_ID,
			orderIndex: ctx.newRuleOrder,
			formElementId: formElementId,
			targetId: null,
			optionId: optionId,
			ruleType: null,
			isConditionEditing: true,
			isActionEditing: false,
			isAddingMode: true,
			rollupEnabled: false,
			condition: {
				operands: [
					{
						type: RULE_STRUCTURE_TYPES.GROUP,
						operands: [
							{
								type: RULE_STRUCTURE_TYPES.SINGLE,
								id: uuidv4(),
								operand: {
									optionType: getOptionType(
										formElementId,
										optionId
									),
									formElementId: formElementId,
									optionId: optionId,
									formElementType: getFormElementType(
										formElementId,
										ctx.formElements
									),
								},
							},
						],
					},
				],
				type: RULE_STRUCTURE_TYPES.GROUP,
			},
		};

		const updatedRules = [ ...ctx.rules, rule ];

		ctx.setRules(updatedRules);
		ctx.setEditingCondition(true);
		ctx.setFormChanged(true);
	};

	const addGroup = ({ rule }) => {
		ctx.setEditingOperand(true);
		rule.condition.operands.push({
			type: RULE_STRUCTURE_TYPES.GROUP,
			operator: "AND",
			operands: [
				{
					type: RULE_STRUCTURE_TYPES.SINGLE,
					id: uuidv4(),
					operand: {},
				},
			],
		});
		ctx.setFormChanged(true);
	};

	const addOperand = ({ group }) => {
		ctx.setEditingOperand(true);
		group.operands.push({
			type: RULE_STRUCTURE_TYPES.SINGLE,
			operator: "AND",
			operand: {},
			id: uuidv4(),
		});
		ctx.setFormChanged(true);
	};

	const deleteOperand = id => {
		let updatedRules = deepCopy(ctx.rules);
		let deletedRule = updatedRules.find(
			rule =>
				rule.condition &&
                rule.condition.operands.find(group =>
                	group.operands.find(operand => operand.id === id)
                )
		);

		updatedRules = updatedRules.filter(
			rule => rule.id !== deletedRule.id
		);

		const groups = deletedRule.condition.operands
			.map(group => ({
				...group,
				operands: group.operands.filter(operand => operand.id !== id),
			}))
			.filter(group => group.operands.length > 0);

		if (groups.length > 0) {
			updatedRules.push({
				...deletedRule,
				condition: {
					...deletedRule.condition,
					operands: groups,
				},
			});
			ctx.setRules(updatedRules);
		} else {
			ctx.setRules(updatedRules);
			updateRules(ctx.formId, updatedRules).then(() => {
				ctx.setFormChanged(true);
				ctx.setEditingCondition(false);
			});
		}
	};

	const addCondition = ({ operand, formElementId, optionId }) => {
		operand.operand = {
			optionType: getOptionType(formElementId, optionId),
			formElementId: formElementId,
			optionId: optionId,
			formElementType: getFormElementType(
				formElementId,
				ctx.formElements
			),
		};
		ctx.setFormChanged(true);
	};

	const saveRules = (options = {}) => {
		const { shouldIncludeNewRules } = options;
		const rules = convertRules(ctx.rules);
		const mutableRules = [ ...rules ];

		if (!shouldIncludeNewRules) {
			const newRuleIndex = mutableRules.findIndex(
				rule => rule.id === NEW_RULE_ID
			);

			if (newRuleIndex > -1) {
				mutableRules.splice(newRuleIndex, 1);
			}
		}

		return updateRules(ctx.formId, mutableRules).then(() => {
			ctx.setEditingAction(false);
			ctx.setFormChanged(false);
			ctx.setEditingCondition(false);
			sortRules();
		});
	};

	const removeRule = id => {
		const newRules = ctx.rules.filter(rule => rule.id !== id);
		ctx.setRules(newRules);

		return updateRules(ctx.formId, newRules);
	};

	return {
		...ctx,
		handleRuleChange,
		sortRules,
		getNewRuleIndex,
		addRule,
		addGroup,
		addOperand,
		deleteOperand,
		addCondition,
		saveRules,
		removeRule,
	};
};
